// CHANGE LOG — Role-Based Settings Menu with Sub-menu
// - Settings menu visible only for Admin, Director, and HR roles
// - Expandable sub-menu for Configuration, Public Documents, and Announcements
// - Documents accessible to all roles
// - Active state detection for settings routes
// - Mobile bottom nav updated with role check

import React, { useState, useEffect, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation, useNavigate } from 'react-router';
import LoadingSpinner from '@components/common/atoms/LoadingSpinner';
import { useAuth } from '@hooks/useAuth';
import { usePermission } from '@/context/PermissionsContext';
import { useToast } from '@/context/ToastContext';
import { useNotifications } from '@/hooks/useNotifications';
import Icon from '@components/common/atoms/Icon';
import ThemeToggle from '@components/common/atoms/ThemeToggle';
import Avatar from '@components/common/atoms/Avatar';
import { EmployeeSearchModal } from '@/features/employees';
import NotificationItem from '@components/notifications/NotificationItem';
import { AppLoader } from '@components/common/atoms/AppLoader';
import { APP_NAME } from '@/config/appConfig';
import { useAssetsAccess } from '@/features/assets/useAssetsAccess';
import { usePageHeaderContext } from '@/context/PageHeaderContext';

interface NavPermission {
  module: string;
  action: string;
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
  isPlaceholder?: boolean;
  requiredRoles?: string[];
  /** Shown only when the user holds this permission (module.action). */
  requiredPermission?: NavPermission;
  subItems?: SubNavItem[];
  /** Keep the group header even when only one sub-item is visible (no collapse to a plain link). */
  keepGroup?: boolean;
}

interface SubNavItem {
  label: string;
  path: string;
  icon: string;
  requiredPermission?: NavPermission;
}

const AppShell: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { can } = usePermission();
  const toast = useToast();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [expandedNotification, setExpandedNotification] = useState<string | null>(null);
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    error: notificationsError,
    refresh: refreshNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications();
  const { breadcrumb } = usePageHeaderContext();

  // Only admins (settings managers) can search the full employee directory
  const canSearchEmployees = can('settings', 'manage');

  // Open employee search with ⌘K / Ctrl+K
  useEffect(() => {
    if (!canSearchEmployees) return;
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [canSearchEmployees]);

  // Close the notification panel on Escape, matching the other overlays.
  useEffect(() => {
    if (!showNotifications) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowNotifications(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showNotifications]);

  const handleNotificationToggle = (id: string) => {
    setExpandedNotification((prev) => (prev === id ? null : id));
    const target = notifications.find((n) => n.id === id);
    if (target && !target.isRead) markAsRead(id);
  };

  /**
   * Contents of the notification panel, shared by the mobile sheet and the desktop dropdown.
   * Only the shell around it differs — the two cannot be one element because the sheet has to
   * be portalled out of the blur-filtered header to position against the viewport.
   */
  const notificationPanel = (
    <>
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white min-w-0 truncate">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 text-xs font-medium text-indigo-600 dark:text-indigo-400">
              {unreadCount} unread
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
              Mark all read
            </button>
          )}
          {/* Tapping outside closes it too, but a sheet needs a visible way out */}
          <button
            onClick={() => setShowNotifications(false)}
            className="sm:hidden p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close notifications"
          >
            <Icon name="X" size={18} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* List — the only part that scrolls, at any width */}
      <div className="flex-1 overflow-y-auto overscroll-contain sm:flex-none sm:max-h-96">
        {notificationsLoading && notifications.length === 0 ? (
          <div className="px-4 py-10 flex justify-center">
            <AppLoader size="small" label="Loading notifications" />
          </div>
        ) : notificationsError && notifications.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <Icon name="AlertTriangle" size={28} className="mx-auto mb-2 text-rose-400" />
            <p className="text-sm text-gray-500 dark:text-gray-400">{notificationsError}</p>
            <button
              onClick={refreshNotifications}
              className="mt-3 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Retry
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <Icon name="BellOff" size={28} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
          </div>
        ) : (
          notifications.map(n => (
            <NotificationItem
              key={n.id}
              notification={n}
              expanded={expandedNotification === n.id}
              onToggle={handleNotificationToggle}
              onOpen={() => setShowNotifications(false)}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => {
            setShowNotifications(false);
            navigate('/notifications');
          }}
          className="w-full py-3 sm:py-2 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          View all notifications
        </button>
      </div>
    </>
  );

  // Assets is limited to the API's Assets:AllowedEmails list — the same list its endpoints
  // enforce. null while the answer is in flight, so the entry never flashes and disappears.
  const assetsAllowed = useAssetsAccess();

  // Check if user has required role
  const hasRole = (requiredRoles?: string[]): boolean => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    return user?.role ? requiredRoles.includes(user.role) : false;
  };

  const navItems: NavItem[] = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'My Profile', path: '/profile', icon: 'UserCircle' },
    { label: 'Attendance', path: '/attendance', icon: 'Clock' },
    { label: 'Notifications', path: '/notifications', icon: 'Bell' },
    { label: 'Leave', path: '/leave', icon: 'Calendar' },
    { label: 'Work From Home', path: '/wfh', icon: 'MonitorSmartphone' },
    { label: 'Request Approvals', path: '/leave/approvals', icon: 'CalendarCheck', requiredRoles: ['Admin', 'Director', 'HR', 'Manager'] },
    {
      label: 'Offboarding',
      path: '/offboarding',
      icon: 'DoorOpen',
      subItems: [
        { label: 'My Offboarding', path: '/offboarding', icon: 'User' },
        { label: 'Approvals', path: '/offboarding/approvals', icon: 'UserCheck', requiredPermission: { module: 'offboarding', action: 'approve' } },
        { label: 'All Records', path: '/admin/offboarding', icon: 'ClipboardList', requiredPermission: { module: 'offboarding', action: 'view' } },
      ],
    },
    { label: 'Documents', path: '/documents', icon: 'FolderOpen' },
    // No requiredRoles: the Assets:AllowedEmails allowlist is the gate now, and it is
    // strictly narrower than the old role list — keeping both would hide the module from an
    // allowlisted user who happens not to be Admin/Director/HR/IT.
    { label: 'Assets', path: '/assets', icon: 'Package' },
    { label: 'Asset Allocation', path: '/assets/allocations', icon: 'UserCheck' },
    { label: 'Ticket Management', path: '/ticket-management', icon: 'Ticket' },
    {
      label: 'Performance',
      path: '/goals',
      icon: 'TrendingUp',
      keepGroup: true,
      subItems: [
        { label: 'My Goals', path: '/goals', icon: 'Target' },
        { label: 'Team Goals', path: '/team-goals', icon: 'Users', requiredPermission: { module: 'goals', action: 'view' } },
        { label: 'KRA Management', path: '/admin/kra', icon: 'ListChecks', requiredPermission: { module: 'goals', action: 'manage' } },
      ],
    },
    // { label: 'Reports', path: '/reports', icon: 'BarChart', isPlaceholder: true },
    // Admin Leave Tools link (visible only to Admin/SuperAdmin)
    // { label: 'Admin Leave Tools', path: '/admin/leave-tools', icon: 'Tool', requiredRoles: ['Admin', 'SuperAdmin'] },
    {
      label: 'Settings',
      path: '/settings',
      icon: 'Settings',
      requiredRoles: ['Admin', 'Director', 'HR'],
      subItems: [
        { label: 'Configuration', path: '/settings/configuration', icon: 'SlidersHorizontal' },
        { label: 'User Management', path: '/settings/users', icon: 'Users' },
        { label: 'Organisation Tree', path: '/settings/org-tree', icon: 'Network' },
        { label: 'Public Documents', path: '/settings/public-documents', icon: 'FolderOpen' },
        { label: 'Announcements', path: '/settings/announcements', icon: 'Megaphone' },
      ],
    },
    // Sits after Settings. Admin-only: the link is hidden by role here, and the route
    // and API both require the reports.manage permission.
    { label: 'Reports', path: '/reports', icon: 'BarChart3', requiredRoles: ['Admin'] },

  ];

  const hasPermission = (p?: NavPermission): boolean => !p || can(p.module, p.action);

  // Filter nav items based on role, permission, and on the Assets allowlist for the asset
  // entries. Sub-items are filtered the same way; a group left with a single sub-item
  // collapses to a plain link *named after that screen* — e.g. a Team Member sees
  // "My Offboarding", not an "Offboarding" group holding one entry. Groups flagged
  // `keepGroup` (Performance) stay a group for every role so the menu is consistent.
  const visibleNavItems = navItems
    .filter(item =>
      hasRole(item.requiredRoles) && hasPermission(item.requiredPermission) &&
      (!item.path.startsWith('/assets') || assetsAllowed === true)
    )
    .map(item => {
      if (!item.subItems) return item;
      const subItems = item.subItems.filter(s => hasPermission(s.requiredPermission));
      if (subItems.length > 1 || (item.keepGroup && subItems.length > 0)) return { ...item, subItems };
      const [only] = subItems;
      return only
        ? { ...item, label: only.label, path: only.path, icon: only.icon, subItems: undefined }
        : { ...item, subItems: undefined };
    });

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/profile') return 'My Profile';
    if (path === '/attendance') return 'Attendance';
    if (path === '/notifications') return 'Notifications';
    if (path === '/assets') return 'Assets Management';
    if (path === '/assets/allocations') return 'Asset Allocation';
    if (path === '/ticket-management') return 'Ticket Management';
    if (path === '/reports') return 'Reports';
    if (path.startsWith('/settings')) {
      if (path.includes('/configuration/')) {
        const parts = path.split('/');
        const configPage = parts[parts.length - 1];
        return configPage.charAt(0).toUpperCase() + configPage.slice(1).replace(/-/g, ' ');
      }
      if (path === '/settings/configuration') return 'Configuration';
      if (path === '/settings') return 'Settings';
    }
    return APP_NAME;
  };

  const handleNavigation = (item: NavItem) => {
    if (item.isPlaceholder) {
      toast.info(`${item.label} feature coming soon!`);
      return;
    }

    if (item.subItems && item.subItems.length > 0) {
      // Toggle sub-menu expansion
      setExpandedMenu(expandedMenu === item.label ? null : item.label);
    } else {
      navigate(item.path);
    }
  };

  const handleSubItemNavigation = (subItem: SubNavItem) => {
    navigate(subItem.path);
    setExpandedMenu(null); // Close sub-menu after navigation
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isSettingsActive = () => {
    return location.pathname.startsWith('/settings');
  };

  const isPerformanceActive = () => {
    return (
      location.pathname.startsWith('/goals') ||
      location.pathname.startsWith('/team-goals') ||
      location.pathname.startsWith('/admin/kra')
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex overflow-x-hidden w-full">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 fixed h-screen z-30">
        {/* Logo */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="absolute inset-0 bg-indigo-500 rounded-xl blur-lg opacity-50"></div>
              {/* logo */}
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {APP_NAME}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Enterprise Suite</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item, index) => {
            const active = isActive(item.path) && !item.isPlaceholder;
            const settingsActive = item.label === 'Settings' && isSettingsActive();
            const performanceActive = item.label === 'Performance' && isPerformanceActive();
            const isExpanded = expandedMenu === item.label || performanceActive;

            return (
              <div key={index}>
                {/* Main Nav Item */}
                <button
                  onClick={() => handleNavigation(item)}
                  onDoubleClick={() => {
                    if (item.subItems && item.subItems.length > 0) {
                      navigate(item.path);
                      setExpandedMenu(null);
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 ${active || settingsActive
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                >
                  <Icon name={item.icon} size={20} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.subItems && (
                    <Icon
                      name="ChevronDown"
                      size={16}
                      className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                        }`}
                    />
                  )}
                </button>

                {/* Sub-menu Items */}
                {item.subItems && isExpanded && (
                  <div className="mt-1 ml-4 space-y-1 animate-in slide-in-from-top-2 duration-200">
                    {item.subItems.map((subItem, subIndex) => (
                      <button
                        key={subIndex}
                        onClick={() => handleSubItemNavigation(subItem)}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${isActive(subItem.path)
                          ? 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                          }`}
                      >
                        <Icon name={subItem.icon} size={18} />
                        <span>{subItem.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Card */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-800">
            <Avatar
              name={user?.name || 'User'}
              src={user?.avatar}
              size="sm"
              className="flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.role || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 w-full min-w-0 pb-20 lg:pb-0">
        {/* Top Navbar */}
        <header className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800">
          <div className="px-4 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-2">
            {/* Page Title / Breadcrumb */}
            {breadcrumb ? (
              <nav
                aria-label="Breadcrumb"
                className="flex items-center gap-2 min-w-0 flex-1 text-lg sm:text-xl lg:text-2xl truncate"
              >
                {breadcrumb.map((item, index) => {
                  const isLast = index === breadcrumb.length - 1;
                  return (
                    <React.Fragment key={`${item.label}-${index}`}>
                      {index > 0 && (
                        <span className="text-gray-300 dark:text-gray-600 font-normal shrink-0">/</span>
                      )}
                      {item.to && !isLast ? (
                        <button
                          onClick={() => navigate(item.to as string)}
                          className="font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors truncate"
                        >
                          {item.label}
                        </button>
                      ) : (
                        <span className="font-bold text-gray-900 dark:text-white truncate">
                          {item.label}
                        </span>
                      )}
                    </React.Fragment>
                  );
                })}
              </nav>
            ) : (
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white truncate min-w-0 flex-1">
                {getPageTitle()}
              </h2>
            )}

            {/* Right Actions — tighter on phones so the bell and badge are not pushed off */}
            <div className="flex items-center gap-1 sm:gap-4">
              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Employee Search (Admin only) */}
              {canSearchEmployees && (
                <button
                  onClick={() => setShowSearch(true)}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Search employees (⌘K)"
                  aria-label="Search employees"
                >
                  <Icon name="Search" size={20} className="text-gray-600 dark:text-gray-400" />
                </button>
              )}

              {/* Notifications */}
              <div className="relative">
                <button
                  className="relative p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <Icon name="Bell" size={20} className="text-gray-600 dark:text-gray-400" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-rose-500 text-white text-xs rounded-full flex items-center justify-center font-bold px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <>
                    {/*
                      The backdrop and the mobile sheet are portalled to <body>. They cannot
                      live in the header: it carries `backdrop-blur-lg`, and a backdrop-filter
                      ancestor becomes the containing block for `position: fixed` descendants —
                      so a fixed child is trapped inside the ~60px header strip instead of
                      filling the viewport.
                    */}
                    {createPortal(
                      <>
                        {/* Dimmed on mobile where the panel is a sheet; click-away everywhere.
                            Must sit BELOW the header (z-40): the header's backdrop-blur makes it a
                            stacking context, so the desktop dropdown's z-50 only ranks inside the
                            header — a z-40 backdrop at body level would paint over the dropdown and
                            eat every click in it. */}
                        <div
                          className="fixed inset-0 z-30 bg-gray-900/40 sm:bg-transparent"
                          onClick={() => setShowNotifications(false)}
                        />

                        {/* Mobile: a sheet pinned between the header and the bottom nav, so its
                            height follows the viewport at any screen size. */}
                        <div
                          role="dialog"
                          aria-label="Notifications"
                          className="sm:hidden fixed inset-x-3 top-16 bottom-24 z-50 flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700"
                        >
                          {notificationPanel}
                        </div>
                      </>,
                      document.body
                    )}

                    {/* sm and up: the bell-anchored dropdown, positioned against this header */}
                    <div
                      role="dialog"
                      aria-label="Notifications"
                      className="hidden sm:flex absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] z-50 flex-col overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700"
                    >
                      {notificationPanel}
                    </div>
                  </>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <Avatar name={user?.name || 'User'} src={user?.avatar} size="sm" />
                  <Icon
                    name="ChevronDown"
                    size={16}
                    className="text-gray-600 dark:text-gray-400"
                  />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-20 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {user?.name || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {user?.email || 'user@example.com'}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Icon name="UserCircle" size={16} />
                        <span>View Profile</span>
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                      >
                        <Icon name="LogOut" size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 w-full max-w-full overflow-x-hidden">
          <Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Bottom Navigation - Mobile */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] z-40">
        <div className="flex items-stretch">
          {[
            { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
            { label: 'Profile', path: '/profile', icon: 'UserCircle' },
            { label: 'Attendance', path: '/attendance', icon: 'Clock' },
            { label: 'Leave', path: '/leave', icon: 'CalendarOff' },
            { label: 'Tickets', path: '/ticket-management', icon: 'Ticket' },
            hasRole(['Admin', 'Director', 'HR'])
              ? { label: 'Settings', path: '/settings', icon: 'Settings' }
              : { label: 'More', path: '/more', icon: 'Menu', isPlaceholder: true },
            // `.filter(Boolean)` never dropped anything — requiredRoles was dead
            // metadata, so every user got the Tickets tab and the row overflowed.
          ].map((item, index) => {
            const active = isActive(item.path) && !item.isPlaceholder;
            // flex-1 + min-w-0: fixed px-4 padding meant each tab was as wide as
            // its label and none could shrink, pushing the last tab off-screen.
            return (
              <button
                key={index}
                onClick={() => {
                  if (item.isPlaceholder) {
                    toast.info(`${item.label} feature coming soon!`);
                  } else {
                    navigate(item.path);
                  }
                }}
                className={`flex-1 min-w-0 flex flex-col items-center gap-1 px-1 py-2 rounded-xl transition-colors ${active
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400'
                  }`}
              >
                <Icon name={item.icon} size={22} />
                <span className="w-full truncate text-center text-[11px] leading-tight font-medium">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Employee Search Popup (Admin only) */}
      {canSearchEmployees && (
        <EmployeeSearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />
      )}
    </div>
  );
};

export default AppShell;
